import {
  ComputeProvisioningEligibility,
  ComputeProvisioningItem,
  ComputeProvisioningPhase,
  ComputeProvisioningPhaseName
} from '@schema';
import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_CLOCK,
  STATUS_ICON_INACTIVE,
  STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O
} from '@/common/utils/statuses';
import computedLocale from '@/common/utils/computedLocale';
import i18n from '@/common/i18n';

export type ComputeProvisioningPhaseState = ComputeProvisioningPhase.state;
export const ComputeProvisioningPhaseState = ComputeProvisioningPhase.state;

export const COMPUTE_PROVISIONING_PHASE_ORDER: ComputeProvisioningPhaseName[] = [
  ComputeProvisioningPhaseName.VALIDATING,
  ComputeProvisioningPhaseName.CONFIGURING_NETWORK,
  ComputeProvisioningPhaseName.CONFIGURING_STORAGE,
  ComputeProvisioningPhaseName.RESOLVING_TIERS,
  ComputeProvisioningPhaseName.DEPLOYING_COMPUTE,
  ComputeProvisioningPhaseName.CREATING_POLICIES,
  ComputeProvisioningPhaseName.CONFIGURING_INTEGRATIONS,
  ComputeProvisioningPhaseName.RECONCILING
];

export const COMPUTE_PROVISIONING_PHASE_TEXT = computedLocale<Record<ComputeProvisioningPhaseName, string>>(() => ({
  [ComputeProvisioningPhaseName.VALIDATING]: i18n.t('Validating the configuration'),
  [ComputeProvisioningPhaseName.CONFIGURING_NETWORK]: i18n.t('Configuring the network'),
  [ComputeProvisioningPhaseName.CONFIGURING_STORAGE]: i18n.t('Configuring the storage cluster'),
  [ComputeProvisioningPhaseName.RESOLVING_TIERS]: i18n.t('Resolving the storage tiers'),
  [ComputeProvisioningPhaseName.DEPLOYING_COMPUTE]: i18n.t('Deploying the compute cluster'),
  [ComputeProvisioningPhaseName.CREATING_POLICIES]: i18n.t('Creating the storage policies'),
  [ComputeProvisioningPhaseName.CONFIGURING_INTEGRATIONS]: i18n.t('Configuring the integrations'),
  [ComputeProvisioningPhaseName.RECONCILING]: i18n.t('Reconciling the cluster state')
}));

export const COMPUTE_PROVISIONING_PHASE_STATE_TEXT = computedLocale<Record<ComputeProvisioningPhaseState, string>>(() => ({
  [ComputeProvisioningPhaseState.PENDING]: i18n.t('Pending'),
  [ComputeProvisioningPhaseState.RUNNING]: i18n.t('In progress'),
  [ComputeProvisioningPhaseState.SUCCEEDED]: i18n.t('Done'),
  [ComputeProvisioningPhaseState.FAILED]: i18n.t('Failed'),
  [ComputeProvisioningPhaseState.SKIPPED]: i18n.t('Skipped'),
  [ComputeProvisioningPhaseState.CANCELED]: i18n.t('Cancelled')
}));

export const COMPUTE_PROVISIONING_PHASE_STATE_ICON: Record<ComputeProvisioningPhaseState, string> = {
  [ComputeProvisioningPhaseState.PENDING]: STATUS_ICON_CLOCK,
  [ComputeProvisioningPhaseState.RUNNING]: STATUS_ICON_LOADING,
  [ComputeProvisioningPhaseState.SUCCEEDED]: STATUS_ICON_CHECK_O,
  [ComputeProvisioningPhaseState.FAILED]: STATUS_ICON_TIMES_O,
  [ComputeProvisioningPhaseState.SKIPPED]: STATUS_ICON_INACTIVE,
  [ComputeProvisioningPhaseState.CANCELED]: STATUS_ICON_INACTIVE
};

export const COMPUTE_PROVISIONING_ITEM_STATE_ICON: Record<ComputeProvisioningItem.state, string> = {
  [ComputeProvisioningItem.state.PENDING]: STATUS_ICON_CLOCK,
  [ComputeProvisioningItem.state.RUNNING]: STATUS_ICON_LOADING,
  [ComputeProvisioningItem.state.SUCCEEDED]: STATUS_ICON_CHECK_O,
  [ComputeProvisioningItem.state.FAILED]: STATUS_ICON_TIMES_O
};

export const COMPUTE_PROVISIONING_ELIGIBILITY_REASON_TEXT = computedLocale<Record<ComputeProvisioningEligibility.reason_code, string>>(() => ({
  [ComputeProvisioningEligibility.reason_code.PROVISIONING_IN_PROGRESS]: i18n.t('Compute provisioning is already in progress.'),
  [ComputeProvisioningEligibility.reason_code.PROVISIONING_FAILED]: i18n.t('A previous compute provisioning attempt failed and requires action.'),
  [ComputeProvisioningEligibility.reason_code.NETWORK_OPERATION_IN_PROGRESS]: i18n.t('A network operation is in progress. Please wait for it to complete.'),
  [ComputeProvisioningEligibility.reason_code.STORAGE_OPERATION_IN_PROGRESS]: i18n.t('A storage operation is in progress. Please wait for it to complete.'),
  [ComputeProvisioningEligibility.reason_code.COMPUTE_OPERATION_IN_PROGRESS]: i18n.t('A compute operation is in progress. Please wait for it to complete.'),
  [ComputeProvisioningEligibility.reason_code.STORAGE_ALREADY_PROVISIONED]: i18n.t('The storage cluster is already provisioned. Set up compute from the "Compute" section.'),
  [ComputeProvisioningEligibility.reason_code.COMPUTE_ALREADY_PROVISIONED]: i18n.t('The compute cluster is already provisioned.')
}));

export const REVIEW_AND_CREATE_STEP = 'reviewAndCreate';
